import { useEffect, useState } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import { updateTwilioSettings, getTwilioSettings, sendTestSMS, updateCallForwardingSettings } from '../../lib/twilio';
import { MessageHistory } from './MessageHistory';
import { MessagePoller } from './MessagePoller';
import { toast } from 'react-hot-toast';

interface TwilioSettingsProps {
  businessId: string;
}

export function TwilioSettings({ businessId }: TwilioSettingsProps) {
  const [isEnabled, setIsEnabled] = useState(false);
  const [accountSid, setAccountSid] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [status, setStatus] = useState<'active' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testStatus, setTestStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [callForwardingEnabled, setCallForwardingEnabled] = useState(false);
  const [forwardingNumber, setForwardingNumber] = useState('');
  const [isForwardingSaving, setIsForwardingSaving] = useState(false);

  // Load existing settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const result = await getTwilioSettings(businessId);
        if (result.success && result.settings) {
          const {
            twilio_enabled,
            twilio_account_sid,
            twilio_auth_token,
            VITE_TWILIO_PHONE_NUMBER,
            twilio_status,
            twilio_error_message,
            twilio_call_forwarding_enabled,
            twilio_forwarding_number
          } = result.settings;

          setIsEnabled(twilio_enabled || false);
          setAccountSid(twilio_account_sid || '');
          setAuthToken(twilio_auth_token || '');
          setPhoneNumber(VITE_TWILIO_PHONE_NUMBER || '');
          setStatus(twilio_status || null);
          setErrorMessage(twilio_error_message || null);
          setCallForwardingEnabled(twilio_call_forwarding_enabled || false);
          setForwardingNumber(twilio_forwarding_number || '');
        }
      } catch (error) {
        console.error('Error loading Twilio settings:', error);
        setErrorMessage('Failed to load Twilio settings');
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [businessId]);

  // Handle enable/disable toggle
  const handleToggle = async (checked: boolean) => {
    console.log('Toggle clicked:', { checked, currentState: isEnabled });
    setIsSaving(true);
    setErrorMessage(null);

    try {
      // If enabling and no credentials are set, just update the UI state
      if (checked && !accountSid && !authToken && !phoneNumber) {
        console.log('Enabling without credentials');
        setIsEnabled(true);
        setIsSaving(false);
        return;
      }

      // If disabling, clear the settings
      if (!checked) {
        console.log('Disabling Twilio');
        const result = await updateTwilioSettings({
          businessId,
          accountSid: '',
          authToken: '',
          phoneNumber: '',
          enabled: false
        });

        console.log('Disable result:', result);

        if (!result.success) {
          console.error('Failed to disable:', result.error);
          setErrorMessage(result.error || 'Failed to update Twilio settings');
          setIsEnabled(true); // Keep enabled if update failed
          return;
        }

        // Successfully disabled
        setIsEnabled(false);
        setAccountSid('');
        setAuthToken('');
        setPhoneNumber('');
        setStatus(null);
        setErrorMessage(null);
        return;
      }

      // If enabling with existing credentials, validate them
      if (checked && accountSid && authToken && phoneNumber) {
        console.log('Enabling with credentials');
        const result = await updateTwilioSettings({
          businessId,
          accountSid,
          authToken,
          phoneNumber,
          enabled: true
        });

        console.log('Enable result:', result);

        if (!result.success) {
          console.error('Failed to enable:', result.error);
          setErrorMessage(result.error || 'Failed to update Twilio settings');
          setIsEnabled(false); // Keep disabled if update failed
          return;
        }

        // Reload settings to get the latest status
        const updatedSettings = await getTwilioSettings(businessId);
        console.log('Updated settings:', updatedSettings);

        if (updatedSettings.success && updatedSettings.settings) {
          const {
            twilio_enabled,
            twilio_account_sid,
            twilio_auth_token,
            VITE_TWILIO_PHONE_NUMBER,
            twilio_status,
            twilio_error_message
          } = updatedSettings.settings;

          setIsEnabled(twilio_enabled || false);
          setAccountSid(twilio_account_sid || '');
          setAuthToken(twilio_auth_token || '');
          setPhoneNumber(VITE_TWILIO_PHONE_NUMBER || '');
          setStatus(twilio_status || null);
          setErrorMessage(twilio_error_message || null);
        }
      }
    } catch (error) {
      console.error('Toggle error:', error);
      setErrorMessage('Failed to update Twilio settings');
      setIsEnabled(!checked); // Revert the toggle if failed
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSaving(true);
    setErrorMessage(null);

    try {
      const result = await updateTwilioSettings({
        businessId,
        accountSid,
        authToken,
        phoneNumber,
        enabled: true
      });

      if (!result.success) {
        setErrorMessage(result.error || 'Failed to update Twilio settings');
        return;
      }

      // Reload settings to get the latest status
      const updatedSettings = await getTwilioSettings(businessId);
      if (updatedSettings.success && updatedSettings.settings) {
        setStatus(updatedSettings.settings.twilio_status);
        setErrorMessage(updatedSettings.settings.twilio_error_message);
      }
    } catch (error) {
      console.error('Error saving Twilio settings:', error);
      setErrorMessage('Failed to save Twilio settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestSMS = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSendingTest(true);
    setTestStatus(null);

    try {
      const result = await sendTestSMS(businessId, testPhoneNumber);
      
      if (result.success) {
        setTestStatus({
          success: true,
          message: 'Test message sent successfully! Check your phone.',
        });
      } else {
        setTestStatus({
          success: false,
          message: result.error || 'Failed to send test message',
        });
      }
    } catch (error) {
      setTestStatus({
        success: false,
        message: 'Failed to send test message',
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleSaveCallForwarding = async () => {
    setIsForwardingSaving(true);
    try {
      const result = await updateCallForwardingSettings({
        businessId,
        callForwardingEnabled,
        forwardingNumber
      });

      if (result.success) {
        toast.success('Call forwarding settings saved successfully');
      } else {
        setErrorMessage(result.error || 'Failed to update call forwarding settings');
      }
    } catch (error) {
      console.error('Error saving call forwarding settings:', error);
      setErrorMessage('Failed to save call forwarding settings');
    } finally {
      setIsForwardingSaving(false);
    }
  };

  if (isLoading) {
    return <div>Loading Twilio settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h3 className="text-lg font-medium">Twilio Integration</h3>
          <p className="text-sm text-gray-500">
            Enable SMS notifications using Twilio
          </p>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={handleToggle}
          disabled={isSaving}
          aria-label="Toggle Twilio integration"
        />
      </div>

      {isEnabled && (
        <>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="accountSid">Account SID</Label>
              <Input
                id="accountSid"
                value={accountSid}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAccountSid(e.target.value)}
                placeholder="Enter your Twilio Account SID"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="authToken">Auth Token</Label>
              <Input
                id="authToken"
                type="password"
                value={authToken}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAuthToken(e.target.value)}
                placeholder="Enter your Twilio Auth Token"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <Input
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value)}
                placeholder="Enter your Twilio Phone Number"
                required
              />
              <p className="text-sm text-gray-500">
                Format: +1234567890 (include country code)
              </p>
            </div>

            {errorMessage && (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            {status === 'active' && (
              <Alert>
                <AlertDescription>Twilio integration is active and working correctly.</AlertDescription>
              </Alert>
            )}

            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </form>

          {/* Test SMS Section */}
          <div className="border-t pt-6 mt-6">
            <h4 className="text-lg font-medium mb-4">Test SMS</h4>
            <form onSubmit={handleTestSMS} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="testPhoneNumber">Test Phone Number</Label>
                <Input
                  id="testPhoneNumber"
                  value={testPhoneNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTestPhoneNumber(e.target.value)}
                  placeholder="Enter phone number to test (+1234567890)"
                  required
                />
                <p className="text-sm text-gray-500">
                  Enter the phone number where you want to receive the test message
                </p>
              </div>

              {testStatus && (
                <Alert variant={testStatus.success ? 'default' : 'destructive'}>
                  <AlertDescription>{testStatus.message}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" disabled={isSendingTest || !status || status !== 'active'}>
                {isSendingTest ? 'Sending...' : 'Send Test Message'}
              </Button>
            </form>
          </div>

          {/* Call Forwarding Section */}
          <div className="border-t pt-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-lg font-medium">Call Forwarding</h4>
                <p className="text-sm text-gray-500">
                  Forward incoming calls to your Twilio number to another phone number
                </p>
              </div>
              <Switch
                checked={callForwardingEnabled}
                onCheckedChange={(checked) => setCallForwardingEnabled(checked)}
                disabled={isForwardingSaving || !status || status !== 'active'}
                aria-label="Toggle call forwarding"
              />
            </div>
            
            {callForwardingEnabled && (
              <form className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forwardingNumber">Forwarding Phone Number</Label>
                  <Input
                    id="forwardingNumber"
                    value={forwardingNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForwardingNumber(e.target.value)}
                    placeholder="Enter phone number to forward calls to (+1234567890)"
                    required
                  />
                  <p className="text-sm text-gray-500">
                    Format: +1234567890 (include country code). All incoming calls to your Twilio number will be forwarded to this number.
                  </p>
                </div>
                
                <Button 
                  type="button"
                  onClick={handleSaveCallForwarding}
                  disabled={isForwardingSaving || !forwardingNumber}
                >
                  {isForwardingSaving ? 'Saving...' : 'Save Forwarding Settings'}
                </Button>
              </form>
            )}
          </div>

          {/* Message History */}
          <div className="mt-8 border-t pt-8">
            <MessageHistory businessId={businessId} />
            <MessagePoller
              businessId={businessId}
              enabled={isEnabled}
              accountSid={accountSid}
              authToken={authToken}
              phoneNumber={phoneNumber}
            />
          </div>
        </>
      )}

      <div className="mt-8 text-sm text-gray-600">
        <h4 className="font-medium">Setup Instructions:</h4>
        <ol className="list-decimal list-inside space-y-2 mt-2">
          <li>Sign up for a Twilio account at <a href="https://twilio.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">twilio.com</a></li>
          <li>Get your Account SID and Auth Token from the Twilio Console</li>
          <li>Purchase or use an existing Twilio phone number</li>
          <li>Enter the credentials above and click Save</li>
          <li>Configure your Twilio SMS webhook URL in the Twilio Console:
            <div className="mt-1 ml-4 p-2 bg-gray-100 rounded font-mono text-sm break-all">
              {`${window.location.protocol}//${window.location.host}/api/webhook/sms`}
            </div>
          </li>
          <li>For call forwarding, enable the feature and provide a phone number above</li>
          <li>Configure your Twilio voice webhook URL in the Twilio Console:
            <div className="mt-1 ml-4 p-2 bg-gray-100 rounded font-mono text-sm break-all">
              {`https://idvgoctfaarxiryxprpy.supabase.co/functions/v1/twilio-call-webhook`}
            </div>
          </li>
          <li>Configure your Twilio voice status callback URL:
            <div className="mt-1 ml-4 p-2 bg-gray-100 rounded font-mono text-sm break-all">
              {`https://idvgoctfaarxiryxprpy.supabase.co/functions/v1/twilio-call-status`}
            </div>
          </li>
          <li>Voice calls to your Twilio number will be forwarded to your specified number</li>
          <li>You'll see incoming call notifications in the top-left corner of the screen</li>
        </ol>
      </div>
    </div>
  );
} 